import { Navigate, Outlet, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { defaultLocale, isLocale, type Locale } from '@/lib/i18n/config';
import { getDictionary } from '@/lib/i18n/dictionaries';
import { Header } from '@/components/site/header';
import { Footer } from '@/components/site/footer';
import { WhatsAppFab } from '@/components/site/whatsapp-fab';
import { ScrollToTop } from '@/components/site/scroll-to-top';
import { useLiveData } from '~/hooks/useLiveData';
import { getSettings } from '@/lib/data/public';
import { readSiteUrl } from '@/lib/supabase/env';

const localeHtmlLang: Record<Locale, string> = {
  es: 'es-ES',
  pt: 'pt-BR',
  en: 'en-US',
};

export default function LocaleLayout() {
  const { locale: rawLocale } = useParams<{ locale: string }>();

  if (!rawLocale || !isLocale(rawLocale)) {
    return <Navigate to={`/${defaultLocale}`} replace />;
  }
  const locale: Locale = rawLocale;
  const dict = getDictionary(locale);
  const { data: settings } = useLiveData(getSettings, [locale]);
  const siteUrl = readSiteUrl();

  const organizationLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: settings?.organization_name || 'VPS Laboratorio',
    alternateName: 'VPS Laboratorio de Biometría Vocal',
    url: siteUrl,
    logo: `${siteUrl}/logo-vps.png`,
    description:
      'Identificación humana forense por voz, habla, lenguaje y rostro. Pericias de audio y vídeo y detección de voces clonadas por IA bajo estándares IAFPA, ENFSI y NIST',
    address: settings?.address
      ? {
          '@type': 'PostalAddress',
          streetAddress: settings.address,
          addressLocality: 'Asunción',
          addressCountry: 'PY',
        }
      : undefined,
    email: settings?.email || undefined,
    telephone: settings?.phone || undefined,
    sameAs: [settings?.social_instagram, settings?.social_linkedin].filter(Boolean),
    areaServed: { '@type': 'Country', name: 'Paraguay' },
    knowsAbout: [
      'Forensic phonetics',
      'Voice biometrics',
      'Speaker identification',
      'Deepfake detection',
      'Audio and video forensics',
      'Forensic face identification',
    ],
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Helmet>
        <html lang={localeHtmlLang[locale]} />
        <script type="application/ld+json">{JSON.stringify(organizationLd)}</script>
      </Helmet>
      <ScrollToTop />
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-primary-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-accent"
      >
        {dict.a11y.skipToContent}
      </a>
      <Header locale={locale} dict={dict} />
      <main id="main" tabIndex={-1} className="flex-1 outline-none">
        <Outlet context={{ locale, settings }} />
      </main>
      <Footer locale={locale} dict={dict} settings={settings} />
      {settings?.whatsapp && (
        <WhatsAppFab
          phone={settings.whatsapp}
          label={dict.a11y.whatsapp}
          prefill={dict.a11y.whatsappPrefill}
        />
      )}
    </div>
  );
}
